import IconExpandMore from 'assets/icons/expand-more.svg';
import cn from 'classnames';
import ChipSelector from 'components/chip-selector';
import Toggle from 'components/toggle';
import { PetType } from 'constants/pet-type';
import {
  AllAllergySegmentationIds,
  CatNutritionSegmentationIds,
  NutritionSegmentationIds,
} from 'constants/segmentation';
import { useFilteredNeeds } from 'hooks/use-filtered-needs';
import { DraftState } from 'modules/landingpage/components/need-capture/state/interface';
import { AnimatePresence, m } from 'motion/react';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

const ANIMATION_CONFIG = {
  duration: 0.25,
  ease: 'easeInOut',
} as const;

interface Props
  extends React.DetailedHTMLProps<
    React.LabelHTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  > {
  selectedOption: Pick<DraftState, 'draftNeeds' | 'draftAllergies'>;
  onSelectAllergies: (value: AllAllergySegmentationIds) => void;
  onSelectNeeds: (
    value: NutritionSegmentationIds | CatNutritionSegmentationIds
  ) => void;
  petType: (typeof PetType)[keyof typeof PetType];
}

/**
 * It renders a list of needs options that the user can toggle.
 */
const NeedsSelectorCommon: React.FC<Props> = ({
  selectedOption,
  onSelectAllergies,
  onSelectNeeds,
  className,
  petType,
  ...divProps
}) => {
  const { allergyEntries, filteredNeedsEntries } = useFilteredNeeds({
    petType,
  });
  const [showAllergies, setShowAllergies] = useState(true);
  const { formatMessage } = useIntl();

  const selectedAllergiesText =
    selectedOption.draftAllergies.length > 0
      ? `${formatMessage({ id: 'common:pet-profile:needs-selector:allergens' })}: ${selectedOption.draftAllergies
          .map((allergy) => formatMessage({ id: `common:allergy:${allergy}` }))
          .join(', ')}`
      : '';

  return (
    <div className={cn('mt-2 flex flex-col', className)} {...divProps}>
      {filteredNeedsEntries.map(([key, value], index) => (
        <React.Fragment key={key}>
          <div className="flex items-center justify-between text-sm">
            <div>
              <span className="text-text-light-bg-primary block font-medium">
                <FormattedMessage
                  id={`common:pet-profile:needs-selector:${value}`}
                />
              </span>
              <span className="text-text-light-bg-secondary block font-normal">
                <FormattedMessage
                  id={`common:pet-profile:needs-selector:${value}-description`}
                />
              </span>
              <span data-test={`needs-label-${value}`} />
            </div>

            <Toggle
              isChecked={selectedOption.draftNeeds.includes(value)}
              onChange={() => onSelectNeeds(value)}
              showIcon={false}
              dataTest={`toggle-${value}`}
            />
          </div>
          <AnimatePresence>
            {(value === NutritionSegmentationIds.Intolerance ||
              value === CatNutritionSegmentationIds.IntoleranceCat) &&
              selectedOption.draftNeeds.includes(value) && (
                <m.div
                  className="relative mt-3"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={ANIMATION_CONFIG}
                >
                  <div className="border-grey-100 my-2 h-px w-full border border-dashed" />
                  <div>
                    <span className="text-text-secondary block text-sm font-normal">
                      <FormattedMessage id="common:pet-profile:needs-selector:intolerance-subtitle" />
                    </span>
                  </div>

                  <m.div
                    className="overflow-hidden"
                    initial={false}
                    animate={{
                      height: showAllergies
                        ? 'auto'
                        : selectedOption.draftAllergies.length > 0
                          ? '28px'
                          : 0,
                    }}
                    transition={ANIMATION_CONFIG}
                  >
                    <AnimatePresence mode="wait">
                      {showAllergies ? (
                        <m.div
                          key="expanded"
                          className="mt-2"
                          initial={{ opacity: 0, y: -20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 20 }}
                          transition={ANIMATION_CONFIG}
                        >
                          <div className="flex flex-wrap gap-3">
                            {allergyEntries.map(([key, value]) => (
                              <ChipSelector
                                key={key}
                                content={
                                  <FormattedMessage
                                    id={`common:allergy:${value}`}
                                  />
                                }
                                isSelected={selectedOption.draftAllergies.includes(
                                  value
                                )}
                                onClick={() => onSelectAllergies(value)}
                                dataTest={`chip-selector-allergy-${value}`}
                              />
                            ))}
                          </div>
                        </m.div>
                      ) : selectedOption.draftAllergies.length > 0 ? (
                        <m.div
                          key="collapsed"
                          className="mt-2"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={ANIMATION_CONFIG}
                        >
                          <p
                            className="text-text-light-bg-secondary truncate text-sm leading-6"
                            data-test="selected-allergies-summary"
                          >
                            {selectedAllergiesText}
                          </p>
                        </m.div>
                      ) : null}
                    </AnimatePresence>
                  </m.div>

                  <m.button
                    className={cn(
                      'text-text-light-bg-secondary flex w-full items-center gap-1.5 pt-2 pb-1',
                      showAllergies ? 'mt-2' : 'mt-0'
                    )}
                    onClick={() => setShowAllergies(!showAllergies)}
                    data-test="toggle-allergies-button"
                  >
                    <span className="text-sm font-medium">
                      <FormattedMessage
                        id={`common:pet-profile:needs-selector:${
                          showAllergies ? 'less' : 'more'
                        }-show`}
                      />
                    </span>
                    <m.div
                      animate={{ rotate: showAllergies ? 180 : 0 }}
                      transition={ANIMATION_CONFIG}
                    >
                      <IconExpandMore className="h-5 w-5" />
                    </m.div>
                  </m.button>
                </m.div>
              )}
          </AnimatePresence>
          {index < filteredNeedsEntries.length - 1 && (
            <div className="bg-grey-100 my-5 h-px w-full" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default NeedsSelectorCommon;
